(() => {
  "use strict";

  const SEL = ".vt-stories";
  let floatDismissed = false;

  function parseVideo(urlRaw) {
    const url = String(urlRaw || "").trim();
    const lower = url.toLowerCase();

    const isFile = /\.(mp4|webm|mov)(\?|#|$)/.test(lower);
    if (isFile) return { type: "file", src: url };

    if (lower.includes("vimeo.com")) {
      const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      return { type: "vimeo", id: m ? m[1] : null, src: url };
    }

    if (lower.includes("youtu.be") || lower.includes("youtube.com")) {
      let id = null;
      const m1 = url.match(/youtu\.be\/([a-zA-Z0-9_-]{6,})/);
      const m2 = url.match(/[?&]v=([a-zA-Z0-9_-]{6,})/);
      const m3 = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{6,})/);
      id = (m1 && m1[1]) || (m2 && m2[1]) || (m3 && m3[1]);
      return { type: "youtube", id, src: url };
    }

    return { type: "unknown", src: url };
  }

  function buildEmbed(v, { muted = true } = {}) {
    if (v.type === "vimeo" && v.id) {
      const m = muted ? 1 : 0;
      return `https://player.vimeo.com/video/${v.id}?autoplay=1&muted=${m}&loop=1&title=0&byline=0&portrait=0&playsinline=1`;
    }
    if (v.type === "youtube" && v.id) {
      const m = muted ? 1 : 0;
      return `https://www.youtube-nocookie.com/embed/${v.id}?autoplay=1&mute=${m}&controls=0&playsinline=1&loop=1&playlist=${v.id}&rel=0`;
    }
    return null;
  }

  function el(tag, attrs = {}, children = []) {
    const n = document.createElement(tag);
    Object.entries(attrs).forEach(([k, v]) => {
      if (k === "class") n.className = v;
      else if (k === "html") n.innerHTML = v;
      else if (k.startsWith("on") && typeof v === "function") n.addEventListener(k.slice(2), v);
      else if (v === true) n.setAttribute(k, "");
      else if (v !== false && v != null) n.setAttribute(k, String(v));
    });
    children.forEach((c) => n.appendChild(typeof c === "string" ? document.createTextNode(c) : c));
    return n;
  }

  function normalizeUrl(u) {
    return String(u || "").replace(/&amp;/g, "&").trim();
  }

  function isIOS() {
    const ua = navigator.userAgent || "";
    const i = /iP(ad|hone|od)/.test(ua);
    const macTouch = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1; // iPadOS
    return i || macTouch;
  }

  // trava thumb no iOS (impede theme/lazy de voltar tudo pra 1ª imagem)
  function lockThumb(img, desired) {
    const want = normalizeUrl(desired);
    if (!want) return;

    try { img.removeAttribute("srcset"); img.removeAttribute("sizes"); } catch (e) {}

    const apply = () => {
      const cur = normalizeUrl(img.getAttribute("src") || img.src || "");
      if (cur !== want) img.src = want;
    };

    requestAnimationFrame(apply);
    setTimeout(apply, 120);
    setTimeout(apply, 900);

    const mo = new MutationObserver(() => apply());
    mo.observe(img, { attributes: true, attributeFilter: ["src", "srcset", "sizes"] });
    setTimeout(() => { try { mo.disconnect(); } catch(e){} }, 2500);
  }

  // gera thumbnail a partir de um frame do vídeo (desktop ok, iOS costuma falhar)
  function makeThumbFromVideo(videoSrc, imgEl, opts = {}) {
    const v = parseVideo(videoSrc);
    if (v.type !== "file") return;

    if (!imgEl || imgEl.dataset.vtThumb === "1") return;
    imgEl.dataset.vtThumb = "1";

    const t = typeof opts.time === "number" ? opts.time : 0.15;
    const maxW = typeof opts.maxW === "number" ? opts.maxW : 240;

    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.preload = "metadata";
    video.crossOrigin = "anonymous";
    video.src = v.src;

    let done = false;
    function cleanup() {
      if (done) return;
      done = true;
      video.removeAttribute("src");
      try { video.load(); } catch (e) {}
    }

    function drawFrame() {
      try {
        const vw = video.videoWidth || 0;
        const vh = video.videoHeight || 0;
        if (!vw || !vh) return cleanup();

        const scale = Math.min(1, maxW / vw);
        const cw = Math.max(1, Math.round(vw * scale));
        const ch = Math.max(1, Math.round(vh * scale));

        const canvas = document.createElement("canvas");
        canvas.width = cw;
        canvas.height = ch;

        const ctx = canvas.getContext("2d", { alpha: false });
        ctx.drawImage(video, 0, 0, cw, ch);

        const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
        if (dataUrl && dataUrl.startsWith("data:image/")) {
          imgEl.src = dataUrl;
          imgEl.dataset.vtFromVideo = "1";
        }
      } catch (e) {
      } finally {
        cleanup();
      }
    }

    video.addEventListener("loadedmetadata", () => {
      const dur = Number(video.duration);
      let seekTo = t;
      if (isFinite(dur) && dur > 0) seekTo = Math.min(t, Math.max(0.05, dur - 0.05));

      const doSeek = () => {
        try { video.currentTime = seekTo; } catch (e) { cleanup(); }
      };

      if (video.readyState >= 2) doSeek();
      else video.addEventListener("loadeddata", doSeek, { once: true });
    }, { once: true });

    video.addEventListener("seeked", drawFrame, { once: true });
    video.addEventListener("error", cleanup, { once: true });

    setTimeout(() => cleanup(), 5000);
  }

  function unique(arr) {
    return Array.from(new Set((arr || []).filter(Boolean)));
  }

  function shuffle(arr) {
    const a = (arr || []).slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  function allSameSrc(imgs) {
    const list = imgs.map((im) => normalizeUrl(im.getAttribute("src") || im.src || "")).filter(Boolean);
    if (!list.length) return true;
    return list.every((s) => s === list[0]);
  }

  let UI = null;

  function getUI() {
    if (UI) return UI;

    const modal = el("div", { class: "vt-modal", hidden: true, role: "dialog", "aria-modal": "true" });
    const dialog = el("div", { class: "vt-modal__dialog", tabindex: "-1" });
    const closeBtn = el("button", { class: "vt-modal__close", type: "button", "aria-label": "Fechar" }, ["×"]);
    const prevBtn = el("button", { class: "vt-modal__nav vt-modal__nav--prev", type: "button", "aria-label": "Vídeo anterior" }, ["‹"]);
    const nextBtn = el("button", { class: "vt-modal__nav vt-modal__nav--next", type: "button", "aria-label": "Próximo vídeo" }, ["›"]);
    const soundBtn = el("button", { class: "vt-modal__sound", type: "button" }, ["Ativar som"]);
    const playerWrap = el("div", { class: "vt-modal__player" });

    dialog.appendChild(playerWrap);
    dialog.appendChild(closeBtn);
    dialog.appendChild(prevBtn);
    dialog.appendChild(nextBtn);
    dialog.appendChild(soundBtn);
    modal.appendChild(dialog);
    document.body.appendChild(modal);

    const floater = el("div", { class: "vt-float", "aria-label": "Vídeo flutuante" });
    const floatClose = el("button", { class: "vt-float-close", type: "button", "aria-label": "Fechar vídeo flutuante" }, ["×"]);
    floater.appendChild(floatClose);
    document.body.appendChild(floater);

    let state = { videos: [], index: 0, fallbackThumb: "", muted: true, onChange: null };

    function clearPlayer() { playerWrap.innerHTML = ""; }

    function toggleNav() {
      const show = state.videos.length > 1;
      prevBtn.hidden = !show;
      nextBtn.hidden = !show;
    }

    function renderPlayer({ inFloat = false } = {}) {
      const raw = state.videos[state.index];
      const v = parseVideo(raw);

      if (inFloat) {
        floater.querySelectorAll("video,img,button.vt-float-play").forEach((n) => {
          if (!n.classList.contains("vt-float-close")) n.remove();
        });

        if (v.type === "file") {
          const vid = el("video", { playsinline: true, muted: true, autoplay: true, loop: true });
          vid.src = v.src;
          floater.insertBefore(vid, floatClose);
        } else {
          const img = el("img", { src: state.fallbackThumb || "", alt: "Vídeo do produto" });
          floater.insertBefore(img, floatClose);
          floater.appendChild(el("button", { type: "button", class: "vt-float-play", "aria-label": "Abrir vídeos" }));
        }

        floater.onclick = (ev) => {
          if (ev.target === floatClose) return;
          open(state.videos, state.index, { fallbackThumb: state.fallbackThumb });
        };
        return;
      }

      clearPlayer();
      state.muted = true;
      soundBtn.textContent = "Ativar som";
      toggleNav();

      if (v.type === "file") {
        const vid = el("video", { playsinline: true, muted: true, autoplay: true, loop: true, controls: false });
        vid.src = v.src;
        playerWrap.appendChild(vid);
      } else {
        const src = buildEmbed(v, { muted: true });
        playerWrap.appendChild(el("iframe", {
          src: src || v.src,
          allow: "autoplay; fullscreen; picture-in-picture",
          allowfullscreen: true,
          title: "Vídeo do produto"
        }));
      }
    }

    function open(videos, index = 0, { fallbackThumb = "" } = {}) {
      state.videos = Array.isArray(videos) ? videos.filter(Boolean) : [];
      state.index = Math.max(0, Math.min(index, state.videos.length - 1));
      state.fallbackThumb = fallbackThumb || "";
      if (!state.videos.length) return;

      modal.hidden = false;
      document.documentElement.style.overflow = "hidden";
      renderPlayer({ inFloat: false });
      dialog.focus();

      if (typeof state.onChange === "function") state.onChange(state.index);
    }

    function close() {
      modal.hidden = true;
      document.documentElement.style.overflow = "";
      clearPlayer();
    }

    function step(delta) {
      if (!state.videos.length) return;
      state.index = (state.index + delta + state.videos.length) % state.videos.length;
      renderPlayer({ inFloat: false });
      if (typeof state.onChange === "function") state.onChange(state.index);
    }

    closeBtn.addEventListener("click", close);
    prevBtn.addEventListener("click", () => step(-1));
    nextBtn.addEventListener("click", () => step(1));

    modal.addEventListener("click", (e) => { if (e.target === modal) close(); });

    document.addEventListener("keydown", (e) => {
      if (modal.hidden) return;
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") step(-1);
      if (e.key === "ArrowRight") step(1);
    });

    soundBtn.addEventListener("click", () => {
      const raw = state.videos[state.index];
      const v = parseVideo(raw);
      state.muted = !state.muted;

      const vid = playerWrap.querySelector("video");
      if (vid) {
        vid.muted = state.muted;
        if (!state.muted) vid.volume = 1;
        soundBtn.textContent = state.muted ? "Ativar som" : "Som ligado";
        return;
      }

      const src = buildEmbed(v, { muted: state.muted });
      const iframe = playerWrap.querySelector("iframe");
      if (iframe && src) iframe.src = src;
      soundBtn.textContent = state.muted ? "Ativar som" : "Som ligado";
    });

    function showFloat(videos, index, { fallbackThumb }) {
      if (floatDismissed) return;

      state.videos = Array.isArray(videos) ? videos.filter(Boolean) : [];
      state.index = Math.max(0, Math.min(index || 0, state.videos.length - 1));
      state.fallbackThumb = fallbackThumb || "";
      if (!state.videos.length) return;

      floater.classList.add("is-on");
      renderPlayer({ inFloat: true });
    }

    function hideFloat() {
      floater.classList.remove("is-on");
      const v = floater.querySelector("video");
      if (v) { try { v.pause(); } catch(e){} }
    }

    floatClose.addEventListener("click", (e) => {
      e.stopPropagation();
      floatDismissed = true;
      hideFloat();
    });

    UI = { open, close, step, showFloat, hideFloat, setOnChange(fn) { state.onChange = fn; } };
    return UI;
  }

  function initOne(container) {
    if (!container || container.dataset.vtInit === "1") return;
    container.dataset.vtInit = "1";

    const script = container.querySelector("script[data-vt-videos]");
    if (!script) return;

    let videos = [];
    try { videos = JSON.parse(script.textContent || "[]"); } catch(e) {}
    videos = Array.isArray(videos) ? videos.filter(Boolean) : [];
    if (!videos.length) return;

    const fallbackThumb = normalizeUrl(container.getAttribute("data-fallback-thumb") || "");
    const floatEnabled = String(container.getAttribute("data-float-enabled") || "true") !== "false";

    const mobile = window.matchMedia("(max-width: 749px)").matches;
    const ios = isIOS();

    // ✅ iOS MOBILE: trava os thumbs do Liquid (foto 1/2/3)
    if (mobile && ios) {
      container.querySelectorAll("img.vt-thumb").forEach((img) => {
        const desired = img.getAttribute("data-vt-thumb") || img.getAttribute("src") || "";
        if (desired) lockThumb(img, desired);
      });
    }

    // Pool de imagens do produto (mantém seu fallback)
    let fallbacks = [];
    const fbScript = container.querySelector("script[data-vt-fallbacks]");
    if (fbScript) {
      try { fallbacks = JSON.parse(fbScript.textContent || "[]"); } catch(e) {}
    }
    fallbacks = shuffle(unique(fallbacks.map(normalizeUrl)));

    const tiles = Array.from(container.querySelectorAll(".vt-tile"));
    const imgs = tiles.map((b) => b.querySelector("img.vt-thumb")).filter(Boolean);

    if (fallbacks.length > 1) {
      const shouldApply = imgs.length && (allSameSrc(imgs) || imgs.every((im) => !normalizeUrl(im.getAttribute("src") || im.src)));
      if (shouldApply) {
        const chosen = [];
        imgs.forEach((img, i) => {
          let pick = fallbacks[i % fallbacks.length];
          for (let k = 0; k < fallbacks.length; k++) {
            const cand = fallbacks[(i + k) % fallbacks.length];
            if (cand && !chosen.includes(cand)) { pick = cand; break; }
          }
          img.dataset.vtPlaceholder = pick;
          img.src = pick;
          chosen.push(pick);
        });
      }
    }

    // ✅ NÃO tenta frame no iOS mobile (evita sobrescrever e falhar)
    if (!(mobile && ios)) {
      tiles.forEach((btn) => {
        const idx = Number(btn.getAttribute("data-vt-index") || "0");
        const videoUrl = videos[idx];
        const img = btn.querySelector("img.vt-thumb");
        if (!videoUrl || !img) return;

        const initial = normalizeUrl(img.getAttribute("src") || img.src || "");
        const run = () => makeThumbFromVideo(videoUrl, img, { time: 0.15, maxW: 260 });

        if ("requestIdleCallback" in window) window.requestIdleCallback(run, { timeout: 1200 });
        else setTimeout(run, 50);

        setTimeout(() => {
          const s = normalizeUrl(img.getAttribute("src") || img.src || "");
          if (s.startsWith("data:image/")) return;
          if (fallbacks.length > 1) {
            if (!s || s === initial || s === fallbackThumb) {
              const used = imgs.map((im) => normalizeUrl(im.getAttribute("src") || im.src || "")).filter(Boolean);
              const alt = fallbacks.find((u) => u && !used.includes(u)) || fallbacks.find((u) => u && u !== s) || s;
              if (alt) img.src = alt;
            }
          } else if (!s && fallbackThumb) {
            img.src = fallbackThumb;
          }
        }, 2500);
      });
    }

    const ui = getUI();
    let current = 0;
    ui.setOnChange((i) => { current = i; });

    container.querySelectorAll(".vt-tile").forEach((btn) => {
      btn.addEventListener("click", () => {
        const idx = Number(btn.getAttribute("data-vt-index") || "0");
        current = isNaN(idx) ? 0 : idx;
        ui.open(videos, current, { fallbackThumb });
      });
    });

    if (!floatEnabled) return;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          ui.hideFloat();
          return;
        }
        if (entry.boundingClientRect.top < 0) {
          ui.showFloat(videos, current, { fallbackThumb });
        } else {
          ui.hideFloat();
        }
      });
    }, { threshold: 0.12 });

    io.observe(container);
  }

  function initAll(root = document) {
    root.querySelectorAll(SEL).forEach(initOne);
  }

  if (document.readyState !== "loading") initAll();
  else document.addEventListener("DOMContentLoaded", () => initAll(), { once: true });

  document.addEventListener("shopify:section:load", (e) => initAll(e.target));

  window.addEventListener("pageshow", (e) => {
    if (e.persisted) {
      floatDismissed = false;
      initAll();
    }
  });
})();